import React, { useCallback, useEffect, useMemo, useState } from "react";

import api from "../services/api";
import TransactionModal from "../components/TransactionModal";
import TransactionStatusBadge from "../components/TransactionStatusBadge";

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

const signForType = (type = "") => {
  const normalized = type.toLowerCase();
  if (normalized.includes("withdraw") || normalized.includes("transfer out")) {
    return -1;
  }
  return 1;
};

const statusSummaryLabels = [
  ["pending", "Pending"],
  ["processing", "Processing"],
  ["successful", "Successful"],
  ["rejected", "Rejected"],
];

const Wallet = () => {
  const [summary, setSummary] = useState({
    balance: 0,
    recent_transactions: [],
    pending_requests: [],
    status_counts: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modalType, setModalType] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await api.getWalletSummary();
      setSummary({
        balance: Number(data?.balance ?? data?.wallet_balance ?? 0),
        recent_transactions: Array.isArray(data?.recent_transactions)
          ? data.recent_transactions
          : [],
        pending_requests: Array.isArray(data?.pending_requests) ? data.pending_requests : [],
        status_counts: data?.status_counts || {},
      });
    } catch (err) {
      console.error("wallet-summary", err);
      setError(err.message || "Unable to load wallet information.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    const id = setInterval(loadSummary, 30000);
    return () => clearInterval(id);
  }, [loadSummary]);

  const transactions = useMemo(() => {
    return summary.recent_transactions
      .map((txn, idx) => ({
        id: txn.id ?? idx,
        type: txn.type || txn.transaction_type || "-",
        note: txn.note || "",
        amount: Number(txn.amount || 0) * signForType(txn.type || txn.transaction_type),
        timestamp: txn.timestamp || txn.created_at || txn.date || null,
        status: txn.status || "successful",
        statusLabel: txn.status_label,
        statusHelp: txn.status_help,
        source: txn.source || "wallet",
        affectsBalance: txn.affects_balance !== false,
      }))
      .sort((a, b) => {
        const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return bTime - aTime;
      });
  }, [summary.recent_transactions]);

  const pendingRequests = useMemo(() => {
    return (summary.pending_requests || []).map((txn, idx) => ({
      id: txn.id ?? `pending-${idx}`,
      type: txn.type || txn.transaction_type || "-",
      note: txn.note || "",
      amount: Number(txn.amount || 0) * signForType(txn.type || txn.transaction_type),
      timestamp: txn.timestamp || txn.created_at || txn.date || null,
      status: txn.status || "pending",
      statusLabel: txn.status_label,
      statusHelp: txn.status_help,
    }));
  }, [summary.pending_requests]);

  const closeModal = () => setModalType(null);

  const handleTransactionComplete = () => {
    closeModal();
    loadSummary();
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-lg p-6 shadow">
          <h1 className="text-2xl font-bold text-gray-800">Wallet overview</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage your Ecoops wallet, move funds, and review your latest transactions.
          </p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Available balance</p>
              <p className="text-3xl font-bold text-gray-800">
                {loading ? "..." : formatCurrency(summary.balance)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setModalType("deposit")}
              className="rounded-lg border border-emerald-600 p-4 text-left hover:bg-emerald-50"
            >
              <p className="font-semibold text-emerald-700">Deposit funds</p>
              <p className="text-sm text-gray-500 mt-1">Submit a funding request for review.</p>
            </button>
            <button
              type="button"
              onClick={() => setModalType("withdraw")}
              className="rounded-lg border border-emerald-600 p-4 text-left hover:bg-emerald-50"
            >
              <p className="font-semibold text-emerald-700">Withdraw funds</p>
              <p className="text-sm text-gray-500 mt-1">Request a payout to your linked account.</p>
            </button>
          </div>

          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => setModalType("transfer")}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:border-emerald-500"
            >
              Transfer to another user
            </button>
            <button
              type="button"
              onClick={loadSummary}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:border-emerald-500"
              disabled={loading}
            >
              Refresh
            </button>
          </div>

          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg p-6 shadow">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Transaction status</h2>
              <p className="text-sm text-gray-500 mt-1">
                Requests appear here before they affect your wallet balance.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {statusSummaryLabels.map(([key, label]) => (
                <div key={key} className="rounded-lg border border-gray-200 px-3 py-2 text-center">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {Number(summary.status_counts?.[key] || 0)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {pendingRequests.length ? (
            <div className="mt-5 divide-y divide-gray-100 rounded-lg border border-gray-100">
              {pendingRequests.map((txn) => (
                <div key={txn.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium capitalize text-gray-800">{txn.type}</p>
                      <TransactionStatusBadge status={txn.status} label={txn.statusLabel} />
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{txn.statusHelp || "Waiting for review."}</p>
                    {txn.note ? <p className="mt-1 text-sm text-gray-600">{txn.note}</p> : null}
                  </div>
                  <div className="text-left md:text-right">
                    <p className="font-semibold text-gray-800">{formatCurrency(txn.amount)}</p>
                    <p className="text-xs text-gray-500">
                      {txn.timestamp ? new Date(txn.timestamp).toLocaleString() : "-"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-500">No active wallet requests.</p>
          )}
        </div>

        <div className="bg-white rounded-lg p-6 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Recent transactions</h2>
            <span className="text-sm text-gray-500">Last {transactions.length} entries</span>
          </div>

          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : !transactions.length ? (
            <p className="text-gray-500">No transactions recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3">Amount</th>
                    <th className="py-2 px-3">Note</th>
                    <th className="py-2 px-3">Timestamp</th>
                    <th className="py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => (
                    <tr key={txn.id} className="border-b">
                      <td className="py-2 px-3 capitalize">{txn.type}</td>
                      <td className="py-2 px-3">{formatCurrency(txn.amount)}</td>
                      <td className="py-2 px-3">{txn.note || "-"}</td>
                      <td className="py-2 px-3">
                        {txn.timestamp ? new Date(txn.timestamp).toLocaleString() : "-"}
                      </td>
                      <td className="py-2 px-3">
                        <TransactionStatusBadge status={txn.status} label={txn.statusLabel} />
                        {!txn.affectsBalance && (
                          <p className="mt-1 text-xs text-gray-500">Not posted yet</p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalType && (
        <TransactionModal type={modalType} onClose={closeModal} onSuccess={handleTransactionComplete} />
      )}
    </div>
  );
};

export default Wallet;
